import {
  Badge,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Combobox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  Option,
  Spinner,
  Subtitle1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  tokens,
} from "@fluentui/react-components";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ApiError } from "../api/client";
import {
  commerce,
  type GoodsReceipt,
  type Location,
  type ReceiveLine,
  type Variant,
} from "../api/commerce";
import { baeTypography } from "../theme/baeTheme";
import { PHONE } from "../ui/tokens";
import { LabelSheet } from "./LabelSheet";

const useStyles = makeStyles({
  page: { display: "grid", gap: tokens.spacingVerticalL, alignContent: "start", minWidth: 0 },
  heading: { fontFamily: baeTypography.fontFamilyDisplay, letterSpacing: "-0.02em" },
  panel: {
    display: "grid",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
    [PHONE]: { padding: tokens.spacingHorizontalM },
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    alignItems: "flex-end",
    minWidth: 0,
  },
  // 12rem rather than 14: at 14 the field is wider than a 375px screen once padding is taken off,
  // and flex-wrap cannot rescue a child that is already too wide on its own.
  grow: { flexGrow: 1, minWidth: "12rem" },
  narrow: { width: "8rem" },
  mono: {
    fontFamily: baeTypography.fontFamilyMonospace,
    fontSize: "13px",
    overflowWrap: "anywhere",
  },
  muted: { color: tokens.colorNeutralForeground3 },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    alignItems: "center",
  },
});

type DraftLine = {
  variant: Variant;
  productName: string;
  quantity: string;
  unitCost: string;
};

/**
 * Goods in.
 *
 * Written for someone standing at a delivery with a box in front of them, so the order of the
 * screen follows the order of the job: where it arrived, what is in it, then receive.
 *
 * Nothing here decides anything. Which SKUs are serialised, whether a location can hold stock,
 * what a serial looks like — all of that comes from the API (ADR-0009). The screen only shows it.
 */
export function GoodsInPage() {
  const styles = useStyles();

  const [siteId, setSiteId] = useState<string>("");
  const [locationId, setLocationId] = useState<string>("");
  const [reference, setReference] = useState("");
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [receipt, setReceipt] = useState<GoodsReceipt | null>(null);
  const [showLabels, setShowLabels] = useState(false);

  const locations = useQuery({ queryKey: ["locations"], queryFn: commerce.locations });
  const products = useQuery({
    queryKey: ["products", search],
    queryFn: () => commerce.products(search || undefined),
  });

  const sites = useMemo(
    () => (locations.data ?? []).filter((l) => l.isSite && l.isActive),
    [locations.data],
  );

  // Only places inside the chosen site that can actually hold stock. A zone organises other
  // locations and would be a dead end if offered here.
  const holdingLocations = useMemo<Location[]>(
    () =>
      (locations.data ?? []).filter(
        (l) => l.siteId === siteId && !l.isSite && l.canHoldStock && l.isActive,
      ),
    [locations.data, siteId],
  );

  const variants = useMemo(
    () =>
      (products.data ?? []).flatMap((product) =>
        product.variants.map((variant) => ({ variant, productName: product.name })),
      ),
    [products.data],
  );

  const receive = useMutation({
    mutationFn: () => {
      const payload: ReceiveLine[] = lines.map((line) => ({
        productVariantId: line.variant.id,
        quantity: Number(line.quantity),
        unitCost: Number(line.unitCost),
        storageLocationId: locationId || null,
      }));

      return commerce.receive(siteId, payload, reference.trim() || null, null);
    },
    onSuccess: (result) => {
      setReceipt(result);
      setLines([]);
      setReference("");
    },
  });

  const addLine = (variantId: string) => {
    const found = variants.find((v) => v.variant.id === variantId);
    if (!found || lines.some((l) => l.variant.id === variantId)) return;

    setLines((current) => [
      ...current,
      {
        variant: found.variant,
        productName: found.productName,
        quantity: "1",
        // Pre-filled from the variant's expected cost. The operator overrides it with what was
        // actually paid, which is what moves the weighted average.
        unitCost: found.variant.costPrice?.toString() ?? "",
      },
    ]);
  };

  const updateLine = (variantId: string, patch: Partial<DraftLine>) =>
    setLines((current) =>
      current.map((line) => (line.variant.id === variantId ? { ...line, ...patch } : line)),
    );

  const removeLine = (variantId: string) =>
    setLines((current) => current.filter((line) => line.variant.id !== variantId));

  const totalUnits = lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);

  const canReceive =
    siteId !== "" &&
    lines.length > 0 &&
    lines.every(
      (line) => Number(line.quantity) > 0 && line.unitCost !== "" && Number(line.unitCost) >= 0,
    );

  if (showLabels && receipt) {
    return <LabelSheet units={receipt.units} onDone={() => setShowLabels(false)} />;
  }

  return (
    <div className={styles.page}>
      <Subtitle1 className={styles.heading} as="h2">
        Goods in
      </Subtitle1>

      {receive.error instanceof ApiError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not receive</MessageBarTitle>
            {receive.error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      {receipt && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Received {receipt.receiptNumber}</MessageBarTitle>
            {receipt.totalQuantity} units taken into stock
            {receipt.units.length > 0 ? `, ${receipt.units.length} serialised` : ""}.{" "}
            {receipt.units.length > 0 && (
              <Button appearance="transparent" onClick={() => setShowLabels(true)}>
                Print labels
              </Button>
            )}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.panel}>
        <Body1Strong>Where did it arrive?</Body1Strong>

        <div className={styles.row}>
          <Field label="Store or warehouse" required className={styles.grow}>
            <Combobox
              placeholder={locations.isLoading ? "Loading…" : "Pick a site"}
              value={sites.find((s) => s.id === siteId)?.name ?? ""}
              onOptionSelect={(_, data) => {
                setSiteId(data.optionValue ?? "");
                setLocationId("");
              }}
            >
              {sites.map((site) => (
                <Option key={site.id} value={site.id} text={site.name}>
                  {site.code} — {site.name}
                </Option>
              ))}
            </Combobox>
          </Field>

          <Field
            label="Put it in"
            className={styles.grow}
            hint={
              siteId
                ? "Optional. Leave blank if it is just 'on site' for now."
                : "Pick a site first"
            }
          >
            <Combobox
              disabled={!siteId}
              placeholder="Somewhere on site"
              value={holdingLocations.find((l) => l.id === locationId)?.name ?? ""}
              onOptionSelect={(_, data) => setLocationId(data.optionValue ?? "")}
            >
              {holdingLocations.map((location) => (
                <Option key={location.id} value={location.id} text={location.name}>
                  {location.code} — {location.name}
                </Option>
              ))}
            </Combobox>
          </Field>

          <Field
            label="Supplier reference"
            className={styles.grow}
            hint="Invoice or delivery note number"
          >
            <Input
              value={reference}
              onChange={(_, d) => setReference(d.value)}
              placeholder="INV-8842"
            />
          </Field>
        </div>
      </div>

      <div className={styles.panel}>
        <Body1Strong>What is in it?</Body1Strong>

        <Field label="Find a SKU">
          <Input
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            placeholder="Search by SKU, product code or name"
          />
        </Field>

        {products.isLoading ? (
          <Spinner size="tiny" label="Loading products" />
        ) : variants.length === 0 ? (
          <Caption1 className={styles.muted}>
            No products yet. A SKU has to exist before stock can be logged against it.
          </Caption1>
        ) : (
          <div className={styles.row}>
            <Field label="Add" className={styles.grow}>
              <Combobox
                placeholder="Pick a SKU to add"
                onOptionSelect={(_, data) => data.optionValue && addLine(data.optionValue)}
              >
                {variants.slice(0, 100).map(({ variant, productName }) => (
                  <Option key={variant.id} value={variant.id} text={variant.sku}>
                    {variant.sku} — {productName} {variant.optionSummary}
                  </Option>
                ))}
              </Combobox>
            </Field>
          </div>
        )}

        {lines.length > 0 && (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>SKU</TableHeaderCell>
                <TableHeaderCell>Product</TableHeaderCell>
                <TableHeaderCell>Quantity</TableHeaderCell>
                <TableHeaderCell>Unit cost</TableHeaderCell>
                <TableHeaderCell>Labels</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.variant.id}>
                  <TableCell>
                    <span className={styles.mono}>{line.variant.sku}</span>
                  </TableCell>
                  <TableCell>
                    {line.productName}{" "}
                    <Caption1 className={styles.muted}>{line.variant.optionSummary}</Caption1>
                  </TableCell>
                  <TableCell>
                    <Input
                      className={styles.narrow}
                      type="number"
                      min={1}
                      value={line.quantity}
                      onChange={(_, d) => updateLine(line.variant.id, { quantity: d.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      className={styles.narrow}
                      type="number"
                      min={0}
                      step="0.01"
                      value={line.unitCost}
                      onChange={(_, d) => updateLine(line.variant.id, { unitCost: d.value })}
                    />
                  </TableCell>
                  <TableCell>
                    {line.variant.isSerialised ? (
                      <Badge appearance="tint" color="informative">
                        {Number(line.quantity) || 0} labels
                      </Badge>
                    ) : (
                      <Caption1 className={styles.muted}>none</Caption1>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      appearance="subtle"
                      size="small"
                      onClick={() => removeLine(line.variant.id)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="large"
          disabled={!canReceive || receive.isPending}
          onClick={() => receive.mutate()}
        >
          {receive.isPending ? <Spinner size="tiny" /> : `Receive ${totalUnits} units`}
        </Button>

        {lines.length > 0 && (
          <Body1 className={styles.muted}>
            {lines
              .filter((l) => l.variant.isSerialised)
              .reduce((s, l) => s + (Number(l.quantity) || 0), 0)}{" "}
            labels will be printed
          </Body1>
        )}
      </div>
    </div>
  );
}
